import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import {
  FIELD_COLUMN_MIN_WIDTH,
  TeamCol,
  TEAM_COLUMN_WIDTH,
  getRosterColumnWidths,
  NameCol,
  RosterFieldCol,
} from "./cols";
import type {
  PayrollTeam,
  PayrollRosterDraftMember,
  PayrollRosterField,
  PayrollRosterMember,
} from "./types";

interface UsePayrollRosterColumnsParams {
  fields: PayrollRosterField[];
  teams: PayrollTeam[];
  draftMembers: PayrollRosterDraftMember[];
  editedIds: Set<string>;
  flaggedMembers: Set<PayrollRosterMember>;
  departedIds: Set<string>;
  onOpen: (draft: PayrollRosterDraftMember) => void;
  onDismissSuggestion: (draft: PayrollRosterDraftMember) => void;
  dismissingSuggestionId: string | null;
  busy: boolean;
}

export const usePayrollRosterColumns = ({
  fields,
  teams,
  draftMembers,
  editedIds,
  flaggedMembers,
  departedIds,
  onOpen,
  onDismissSuggestion,
  dismissingSuggestionId,
  busy,
}: UsePayrollRosterColumnsParams) =>
  useMemo(() => {
    const members = draftMembers.map(({ member }) => member);
    const widths = getRosterColumnWidths(members, fields);
    const baseColumns: ColumnDef<PayrollRosterDraftMember>[] = [
      NameCol({
        editedIds,
        flaggedMembers,
        departedIds,
        onOpen,
        onDismissSuggestion,
        dismissingSuggestionId,
        busy,
        width: widths.name,
      }),
    ];

    const fieldColumns = fields.map((field) =>
      RosterFieldCol(
        field,
        widths.fields.get(field.key) ?? FIELD_COLUMN_MIN_WIDTH,
      ),
    );

    return {
      columns: [
        ...baseColumns,
        ...(teams.length > 0 ? [TeamCol<PayrollRosterDraftMember>(teams)] : []),
        ...fieldColumns,
      ],
      tableMinWidth:
        widths.tableMinWidth + (teams.length ? TEAM_COLUMN_WIDTH : 0),
    };
  }, [
    busy,
    dismissingSuggestionId,
    draftMembers,
    departedIds,
    editedIds,
    fields,
    flaggedMembers,
    onDismissSuggestion,
    onOpen,
    teams,
  ]);
